/**
 * Other upcharges (JF 2026-10-13): charges to the customer with no actual cost (or none worth working out), row by row
 * between Additional and the tiered quote. Each row: what it is, an amount per piece or for the whole order, and whether
 * it counts as profit — in GP it is part of the quoted Total FOB; not in GP it is only added to Grand Total FOB.
 */
import { n, perPiece, upchargesOf, type Per, type Upcharge } from "@ic/engine";
import { useMoney } from "../../shared/money.ts";
import { useStore } from "../../store.ts";
import { useT } from "../../shared/i18n.ts";
import { Field } from "../../shared/ui/common.tsx";

export function Upcharges() {
  const t = useT();
  const s = useStore((st) => st.sheet);
  const { editSheet } = useStore.getState();
  const rows = upchargesOf(s);
  const m = useMoney();
  const qty = n(s.h.qty);
  // the first edit turns a sheet's two old amounts into rows
  const write = (fn: (list: Upcharge[]) => Upcharge[]) => editSheet((x) => { x.upcharges = fn(upchargesOf(x).map((u) => ({ ...u }))); x.other = 0; x.otherGp = ""; });
  const set = (i: number, patch: Partial<Upcharge>) => write((l) => { l[i] = { ...l[i], ...patch }; return l; });
  const gp = rows.reduce((a, u) => a + (u.gp ? perPiece(u.amount, u.per, qty) : 0), 0);
  const other = rows.reduce((a, u) => a + (u.gp ? 0 : perPiece(u.amount, u.per, qty)), 0);
  return (
    <div className="sec tint-up" id="sec-up">
      <div className="sec-h"><b>Other upcharge</b>
        <span className="dim">{t("charged to the customer with no actual cost · in GP = part of the quoted FOB and profit; not in GP = only added to Grand Total",
          "向客户收、没有实际成本的费用 · 计入 GP = 算进报价 FOB 和利润；不计入 GP = 只加在 Grand Total 上")}</span>
        <span className="tot" id="tot-up">{t("in GP", "计入 GP")} {m.fmt(gp)} · {t("not in GP", "不计入 GP")} {m.fmt(other)}</span></div>
      <div className="gs"><table className="g" id="upTbl" style={{ minWidth: 700 }}><tbody>
        <tr><th>{t("Description", "说明")}</th><th className="num">{t("Amount", "金额")} {m.sym}</th><th>{t("Per", "计价")}</th><th>{t("In GP", "计入 GP")}</th>
          <th className="num">{t("Per piece", "每件")} {m.sym}</th><th></th></tr>
        {rows.map((u, i) => (
          <tr key={i} data-uprow={i}>
            <td><Field value={u.desc} onChange={(v) => set(i, { desc: v })} placeholder={t("e.g. rush fee, special packing", "例：加急费、特殊包装")} /></td>
            <td><Field className="n" dataK={"up-amt-" + i} value={m.shown(u.amount, u.cny)} onChange={(v) => { const mv = m.typed(v); set(i, { amount: mv.px, cny: mv.cny }); if (!s.fx && m.cur === "CNY") editSheet((x) => { x.fx = m.fx; }); }} placeholder="0.00" /></td>
            <td><select className="in sel" data-up-per={i} value={u.per} onChange={(e) => set(i, { per: e.target.value as Per })}>
              <option value="pc">{t("per piece", "每件")}</option><option value="order">{t("whole order", "整单")}</option></select></td>
            <td><label className="upgp"><input type="checkbox" data-up-gp={i} checked={u.gp} onChange={(e) => set(i, { gp: e.target.checked })} /> {u.gp ? t("in GP", "计入") : t("not in GP", "不计入")}</label></td>
            <td className="calc num">{u.per === "order" && !qty ? t("enter order qty", "请填订单量") : m.num(perPiece(u.amount, u.per, qty))}</td>
            <td><span className="lnk dim" data-up-del={i} onClick={() => write((l) => l.filter((_, j) => j !== i))}>{t("Remove", "删除")}</span></td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={6} className="dim">{t("None.", "暂无。")}</td></tr>}
      </tbody></table></div>
      <div className="addrow"><button className="btn sm" id="upAdd" onClick={() => write((l) => [...l, { desc: "", amount: "", per: "pc", gp: true }])}>＋ {t("Add an upcharge", "新增收费")}</button></div>
    </div>
  );
}
